import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { StreamStats, StatusBarOrientation } from '../types';
import type { PeerInfo } from '../core/SignalingService';
import { useSound } from '../hooks';
import apiClient from '../core/ApiClient';
import { eventBus } from '../core/EventBus';
import { webRTCService } from '../core/WebRTCService';
import { useFileManagerStore } from './FileManager/store/useFileManagerStore';

// GPU指纹生成头像颜色
const generateAvatarColor = (fingerprint: string): string => {
  let seed = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    seed = ((seed << 5) - seed) + fingerprint.charCodeAt(i);
    seed = seed & seed;
  }
  seed = Math.abs(seed);
  const hue = seed % 360;
  return `hsl(${hue}, 60%, 50%)`;
};

interface StatusBarProps {
  stats: StreamStats;
  isVisible: boolean;
  clipboardSync: boolean;
  onToggleClipboard: () => void;
  onToggleQuickActions: () => void;
  onUploadFiles: () => void;
  onUploadDirectory: () => void;
  onDownloadClipboard: () => void;
  onOpenUploadPanel?: () => void;
  uploadingCount?: number;
  onToggleFullscreen: () => void;
  quickActionsActive: boolean;
  isTransferring?: boolean;
  isFullscreen?: boolean;
  connectedPeers?: PeerInfo[];
  onToggleFileManager?: () => void;
  fileManagerActive?: boolean;
  targetBitrate?: number;
  orientation?: StatusBarOrientation;
  // New props (merged from ControlBar)
  onOpenSettings?: () => void;
  settingsActive?: boolean;
  onDisconnect?: () => void;
  inputEnabled?: boolean;
  onToggleInput?: () => void;
  // Canvas zoom/pan
  canvasZoom?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  isPanMode?: boolean;
  onTogglePanMode?: () => void;
  onResetZoom?: () => void;
  // Stats & ICE Racing
  onToggleStats?: () => void;
  statsActive?: boolean;
  onToggleIceRacing?: () => void;
  iceRacingActive?: boolean;
  peerId?: string;
  // 状态栏网吧信息：网吧名称 / 分组 / 终端标识(网吧备注优先，否则座位号) / IP
  venueName?: string;
  venueGroup?: string;
  terminalSeat?: string; // 实际值由 App 计算：网吧备注(name) 非空用备注，否则用座位号(seat)
  terminalIp?: string;
  // 只读模式：true 时隐藏所有向远端发送指令的按钮；常驻显示只读/可控切换开关
  viewOnly?: boolean;
  onToggleViewOnly?: () => void;
  // noauth 免鉴权模式：true 时隐藏依赖 token 的功能入口（解锁/锁定/分享远程）
  noauth?: boolean;
}

// Load/save position from localStorage
const loadPosition = (): { left: number; top: number } | null => {
  try {
    const saved = localStorage.getItem('bladePosition');
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
};

const savePosition = (left: number, top: number) => {
  try {
    localStorage.setItem('bladePosition', JSON.stringify({ left, top }));
  } catch {}
};

const StatusBar: React.FC<StatusBarProps> = ({
  stats,
  clipboardSync,
  onToggleClipboard,
  onToggleQuickActions,
  onUploadFiles,
  onUploadDirectory,
  onDownloadClipboard,
  onOpenUploadPanel,
  uploadingCount = 0,
  onToggleFullscreen,
  quickActionsActive,
  isTransferring = false,
  isFullscreen = false,
  connectedPeers = [],
  onToggleFileManager,
  fileManagerActive = false,
  targetBitrate = 0,
  orientation = 'vertical',
  onOpenSettings,
  settingsActive = false,
  onDisconnect,
  inputEnabled = true,
  onToggleInput,
  canvasZoom = 1,
  onZoomIn,
  onZoomOut,
  isPanMode = false,
  onTogglePanMode,
  onResetZoom,
  onToggleStats,
  statsActive = false,
  onToggleIceRacing,
  iceRacingActive = false,
  peerId,
  venueName = '',
  venueGroup = '',
  terminalSeat = '',
  terminalIp = '',
  viewOnly = false,
  onToggleViewOnly,
  noauth = false,
}) => {
  const isHorizontal = orientation === 'horizontal';
  const hasWarning = stats.packetLoss > 5 || stats.jitter > 80;
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showToolbarMenu, setShowToolbarMenu] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareType, setShareType] = useState<'once' | 'ttl'>('ttl');
  const [shareHours, setShareHours] = useState('5');
  const [shareLoading, setShareLoading] = useState(false);

  // 锁定 / 解锁：调 admin.wwls.net/api/task/forward
  // - merchant_id 从 URL 参数取
  // - seat 从 peerId 按"最后一个 '-'"拆出前半部分（peerId 格式: {seat}-{frp地址}）
  // - 自动带 Bearer token（ApiClient 域名校验）
  const handleControlPc = useCallback(async (action: 'lock' | 'unlock') => {
    const merchantId = new URLSearchParams(window.location.search).get('merchant_id');
    if (!merchantId) {
      eventBus.emit('ui:toast', { message: '缺少 merchant_id 参数', type: 'error' });
      return;
    }
    if (!peerId) {
      eventBus.emit('ui:toast', { message: '缺少 peer 信息', type: 'error' });
      return;
    }
    const sepIdx = peerId.lastIndexOf('-');
    if (sepIdx <= 0) {
      eventBus.emit('ui:toast', { message: 'peer 格式异常，无法解析 seat', type: 'error' });
      return;
    }
    const seat = peerId.substring(0, sepIdx);

    const fd = new FormData();
    fd.append('merchant_id', merchantId);
    fd.append('seat', seat);
    fd.append('event', 'controlPc');
    fd.append('data[type]', action);

    try {
      await apiClient.request('/task/forward', { method: 'POST', formData: fd });
      eventBus.emit('ui:toast', { message: action === 'lock' ? '已锁定' : '已解锁', type: 'success' });
    } catch (e) {
      const msg = action === 'lock' ? '锁定失败' : '解锁失败';
      const detail = (e as Error)?.message;
      eventBus.emit('ui:toast', { message: detail ? `${msg}: ${detail}` : msg, type: 'error' });
    }
  }, [peerId]);

  // 复制文本到剪贴板：优先 Clipboard API，失败回退 execCommand
  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* 回退 */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }, []);

  // 分享远程：调 temp-token 接口拿临时 token，拼到当前 URL 的 access_token 参数后复制
  const handleShare = useCallback(async () => {
    const fd = new FormData();
    fd.append('type', shareType);
    if (shareType === 'ttl') {
      const hours = parseFloat(shareHours);
      if (!Number.isFinite(hours) || hours < 0.1 || hours > 24) {
        eventBus.emit('ui:toast', { message: '有效时长需在 0.1~24 小时之间', type: 'error' });
        return;
      }
      fd.append('duration', String(Math.round(hours * 3600)));
    }

    setShareLoading(true);
    try {
      const resp = await apiClient.request<{ data?: { access_token?: string } }>(
        '/passport/temp-token', { method: 'POST', formData: fd },
      );
      const token = resp?.data?.access_token;
      if (!token) {
        eventBus.emit('ui:toast', { message: '生成失败: 未返回 access_token', type: 'error' });
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('access_token', token);
      const shareUrl = url.toString();
      const ok = await copyToClipboard(shareUrl);
      if (ok) {
        eventBus.emit('ui:toast', { message: '分享链接已复制到剪贴板', type: 'success' });
        setShowShareDialog(false);
      } else {
        eventBus.emit('ui:toast', { message: '复制失败，请手动复制地址栏链接', type: 'error' });
      }
    } catch (e) {
      const detail = (e as Error)?.message;
      eventBus.emit('ui:toast', { message: detail ? `生成失败: ${detail}` : '生成失败', type: 'error' });
    } finally {
      setShareLoading(false);
    }
  }, [shareType, shareHours, copyToClipboard]);
  const [position, setPosition] = useState<{ left: number; top: number }>(
    () => loadPosition() || { left: 50, top: 100 }
  );

  const { onHover: playHover, onClick: playClick } = useSound();

  // Drag state
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const DRAG_THRESHOLD = 5;

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-upload-menu]')) setShowUploadMenu(false);
      if (!target.closest('[data-toolbar-menu]')) setShowToolbarMenu(false);
      if (!target.closest('[data-share-dialog]')) setShowShareDialog(false);
    };
    if (showUploadMenu || showToolbarMenu || showShareDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUploadMenu, showToolbarMenu, showShareDialog]);

  // 打开全局文件搜索：复用文件搜索引擎，无需打开文件管理器即可弹出全局搜索
  const openFileSearch = useCallback(async () => {
    const fmStore = useFileManagerStore.getState();
    // 懒创建 fs-rpc 通道（不依赖文件管理器面板是否打开）
    if (!fmStore.fs.isConnected) {
      const ch = await webRTCService.ensureFsRpcChannel();
      if (ch) await fmStore.initializeDataChannel(ch);
    }
    // 先检查搜索引擎状态：从未初始化→提示初始化；正在初始化→询问是否打开文件管理查看进度；
    // 其它（ready / error）→直接打开搜索面板（面板自带提示）
    const st = await (fmStore.fs as any).getSearchEngineStatus();
    const inProgress = ['initializing', 'indexing', 'building_index', 'calculating_sizes'];
    if (st && st.status === 'not_started') {
      fmStore.openSearchEngineInitModal(false);
    } else if (st && inProgress.includes(st.status)) {
      if (window.confirm('文件搜索引擎正在初始化，是否打开「文件管理」查看进度？')) {
        eventBus.emit('fm:open', undefined as never);
      }
    } else {
      fmStore.openGlobalSearch();
    }
  }, []);

  // Alt+F 快捷键：打开全局文件搜索（由 InputService 拦截并 emit 事件）
  useEffect(() => eventBus.on('file_search:openSearchHotkey', openFileSearch), [openFileSearch]);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const target = e.target as Element;
    if (target.closest('[data-satellite]') || target.closest('[data-upload-menu]') || target.closest('[data-toolbar-menu]') || target.closest('button')) return;

    e.preventDefault();
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: position.left,
      top: position.top,
    };
    isDraggingRef.current = true;
    hasMovedRef.current = false;
  }, [position]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const deltaX = e.clientX - startRef.current.x;
      const deltaY = e.clientY - startRef.current.y;
      if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) < DRAG_THRESHOLD) return;
      hasMovedRef.current = true;
      const padding = 10;
      setPosition({
        left: Math.max(padding, Math.min(window.innerWidth - 50, startRef.current.left + deltaX)),
        top: Math.max(padding, Math.min(window.innerHeight - 50, startRef.current.top + deltaY)),
      });
    };
    const handleMouseUp = () => {
      if (isDraggingRef.current && hasMovedRef.current) {
        savePosition(position.left, position.top);
      }
      isDraggingRef.current = false;
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [position]);

  // Breathing animation class
  const getBreathingClass = () => {
    if (isTransferring) return 'nano-breathe-green';
    if (hasWarning) return 'nano-breathe-yellow';
    return '';
  };

  // Connection detail string
  const getConnectionDetail = () => {
    if (stats.transportType === 'relay') {
      const protocol = stats.relayProtocol || 'TCP';
      return `中转-${protocol}`;
    } else if (stats.transportType === 'p2p') {
      const ip = stats.remoteAddress || '';
      if (ip) {
        if (ip.includes(':')) {
          const parts = ip.split(':');
          return `P2P ${parts[0]}:..`;
        }
        return `P2P ${ip}`;
      }
      return 'P2P';
    }
    return '连接中';
  };

  const getStatusBadge = () => {
    const detail = getConnectionDetail();
    if (hasWarning) return { text: `● 链路波动 (${detail})`, className: 'nano-badge-warning' };
    if (stats.transportType === 'p2p') return { text: `● ${detail}`, className: 'nano-badge-p2p' };
    if (stats.transportType === 'relay') return { text: `● ${detail}`, className: 'nano-badge-relay' };
    return { text: '● 连接中', className: 'nano-badge-warning' };
  };

  const statusBadge = getStatusBadge();
  const peerCount = connectedPeers.length || 1;
  const showVenue = !!(terminalSeat || venueName || venueGroup || terminalIp);

  return (
    <div
      className={`nano-blade-wrap ${isHorizontal ? 'nano-blade-wrap-h' : 'nano-blade-wrap-v'}`}
      style={{ left: position.left, top: position.top }}
      data-ui-element="true"
      onMouseDown={handleMouseDown}
    >
      <div className={`nano-blade ${isHorizontal ? 'nano-blade-horizontal' : ''} ${getBreathingClass()}`}>
      {/* 1. Status Light (P2P=green, TURN=yellow) */}
      <div className="nano-head" data-head="true">
        <div className="nano-eye">
          <div className={`nano-dot ${hasWarning ? 'danger' : stats.transportType === 'relay' ? 'warning' : ''}`} />
        </div>
        {/* Satellite: connection info on hover */}
        <div className={`nano-satellite `} data-satellite="true">
          <div className="nano-sat-header">
            <span className="nano-sat-title">STATUS</span>
            <span className={`nano-sat-badge ${statusBadge.className}`}>{statusBadge.text}</span>
          </div>
          <div className="nano-sat-grid">
            <div className="nano-stat"><div>RTT</div><div>{stats.rtt}ms</div></div>
            <div className="nano-stat"><div>FPS</div><div>{stats.fps}</div></div>
            <div className="nano-stat">
              <div>码率</div>
              <div>{(stats.bitrate / 1000).toFixed(1)}/{targetBitrate > 0 ? (targetBitrate / 1000).toFixed(0) : '?'}Mbps</div>
            </div>
            <div className="nano-stat">
              <div>Codec</div>
              {/* 阶段 2：encoderName 来自 host server_stats 的 dynamic_encoder_name（NVENC AV1 / x264 (Software) / ...），
                  按字面值简化展示；catalog-driven 精确显示在 SettingsPanel / Dashboard 里 */}
              <div>{(() => {
                const n = (stats.encoderName || '').toLowerCase();
                if (n.includes('nvenc')) return stats.videoCodec;
                if (n.includes('libaom')) return 'AV1';
                if (n.includes('openh264')) return 'OpenH264';
                if (n.includes('x264')) return 'x264';
                return stats.videoCodec || '?';
              })()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Online Users */}
      <div className="nano-head" data-head="true">
        <div className="nano-eye" style={{ fontSize: '9px', color: '#aaa', fontFamily: "'JetBrains Mono', monospace", fontWeight: 'bold' }}>
          {peerCount}
        </div>
        {/* Satellite: user list on hover */}
        <div className={`nano-satellite `} data-satellite="true">
          <div className="nano-sat-users">
            <div className="nano-sat-users-header">
              <span>在线用户</span>
              <span className="nano-user-count">{peerCount}</span>
            </div>
            <div className="nano-sat-users-list">
              {(connectedPeers.length > 0 ? connectedPeers : [{ clientId: 'self', ip: '本机', joinTime: Date.now(), fingerprint: 'self' }]).map((peer) => (
                <div key={peer.clientId} className="nano-user-item">
                  <div className="nano-user-avatar" style={{ background: generateAvatarColor(peer.fingerprint) }} />
                  <div className="nano-user-info">
                    <div className="nano-user-ip">{peer.ip}</div>
                    <div className="nano-user-time">{new Date(peer.joinTime).toLocaleTimeString()} 加入</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="nano-divider" />

      {/* 看 / 控 切换开关 — 常驻按钮，点击切换 */}
      <button
        className="nano-btn"
        onClick={() => { playClick(); onToggleViewOnly?.(); }}
        onMouseEnter={playHover}
        title={viewOnly ? '点击切换为控' : '点击切换为看'}
        style={{
          '--btn-color': viewOnly ? '#ef4444' : '#10b981',
          background: viewOnly ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.12)',
          color: viewOnly ? '#ef4444' : '#10b981',
          fontSize: '11px',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
        } as React.CSSProperties}
      >
        {viewOnly ? '看' : '控'}
      </button>

      {/* 3. Quick Actions */}
      {!viewOnly && (
      <button
        className={`nano-btn ${quickActionsActive ? 'active' : ''}`}
        style={{ '--btn-color': '#eab308' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleQuickActions(); }}
        onMouseEnter={playHover}
        title="快捷操作"
      >
        <svg viewBox="0 0 24 24"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>
      </button>
      )}

      {/* 4. Clipboard Sync */}
      {!viewOnly && (
      <button
        className={`nano-btn ${clipboardSync ? 'active' : ''}`}
        style={{ '--btn-color': '#3b82f6' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleClipboard(); }}
        onMouseEnter={playHover}
        title={clipboardSync ? '剪贴板同步 (开)' : '剪贴板同步 (关)'}
      >
        <svg viewBox="0 0 24 24"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
        {clipboardSync && <div className="nano-active-dot" />}
      </button>
      )}

      {/* 5. Upload/Download */}
      {!viewOnly && (
      <div style={{ position: 'relative' }} data-upload-menu>
        <button
          className={`nano-btn ${isTransferring ? 'active' : ''}`}
          style={{ '--btn-color': '#10b981' } as React.CSSProperties}
          onClick={() => { playClick(); setShowUploadMenu(!showUploadMenu); }}
          onMouseEnter={playHover}
          title={uploadingCount > 0 ? `上传中 ${uploadingCount} 个` : "上传/下载"}
        >
          <svg viewBox="0 0 24 24"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
          {isTransferring && (
            <div className="nano-ping">
              <span className="ping" />
              <span className="dot" />
            </div>
          )}
        </button>
        {showUploadMenu && (
          <div className={`nano-menu `}>
            {/* 拉模式：菜单项常驻，点击主动查询远端剪贴板 */}
            <div className="nano-menu-item highlight" onClick={() => { playClick(); onDownloadClipboard(); setShowUploadMenu(false); }} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
              下载远程剪贴板
            </div>
            <div className="nano-menu-item" onClick={() => { playClick(); onUploadFiles(); setShowUploadMenu(false); }} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
              上传文件
            </div>
            <div className="nano-menu-item" onClick={() => { playClick(); onUploadDirectory(); setShowUploadMenu(false); }} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24"><path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10z"/></svg>
              上传目录
            </div>
            {onOpenUploadPanel && (
            <div className="nano-menu-item" onClick={() => { playClick(); onOpenUploadPanel(); setShowUploadMenu(false); }} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24"><path d="M3 5v14h18V5H3zm16 12H5V9h14v8z"/></svg>
              上传面板
            </div>
            )}
          </div>
        )}
      </div>
      )}

      <div className="nano-divider" />

      {/* 6. Toolbar (horizontal icon sub-bar) */}
      <div style={{ position: 'relative' }} data-toolbar-menu>
        <button
          className={`nano-btn ${!inputEnabled || isPanMode ? 'active' : ''}`}
          style={{ '--btn-color': '#f97316' } as React.CSSProperties}
          onClick={() => { playClick(); setShowToolbarMenu(!showToolbarMenu); }}
          onMouseEnter={playHover}
          title="工具栏 [Alt+=/- 缩放]"
        >
          <svg viewBox="0 0 24 24"><path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/></svg>
          {(!inputEnabled || isPanMode) && <div className="nano-active-dot" style={{ background: '#f97316', boxShadow: '0 0 4px #f97316' }} />}
        </button>
        {showToolbarMenu && (
          <div className="nano-toolbar-sub">
            {/* 文件搜索：复用文件搜索引擎，无需打开文件管理器即可弹出全局搜索 */}
            <button
              className="nano-toolbar-btn"
              onClick={() => {
                playClick();
                setShowToolbarMenu(false);
                openFileSearch();
              }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
              <span className="nano-toolbar-tip">文件搜索 [Alt+F]</span>
            </button>
            {/* Unlock / Lock：peerId 必须含 '-' 才能解析 seat；noauth 模式隐藏（依赖 token） */}
            {!noauth && !viewOnly && peerId && peerId.includes('-') && (<>
              <button
                className="nano-toolbar-btn"
                onClick={() => { playClick(); handleControlPc('unlock'); }}
                onMouseEnter={playHover}
              >
                <svg viewBox="0 0 24 24"><path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/></svg>
                <span className="nano-toolbar-tip">解锁</span>
              </button>
              <button
                className="nano-toolbar-btn"
                onClick={() => { playClick(); handleControlPc('lock'); }}
                onMouseEnter={playHover}
              >
                <svg viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
                <span className="nano-toolbar-tip">锁定</span>
              </button>
            </>)}
            {/* Share remote：noauth 模式隐藏（依赖 token 生成临时分享链接） */}
            {!noauth && (
            <button
              className="nano-toolbar-btn"
              onClick={() => { playClick(); setShowToolbarMenu(false); setShowShareDialog(true); }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
              <span className="nano-toolbar-tip">分享远程</span>
            </button>
            )}
            {/* Disable/Enable control */}
            {!viewOnly && (
            <button
              className={`nano-toolbar-btn ${!inputEnabled ? 'active' : ''}`}
              onClick={() => { playClick(); onToggleInput?.(); }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M15 7.5V2H9v5.5l3 3 3-3zM7.5 9H2v6h5.5l3-3-3-3zM9 16.5V22h6v-5.5l-3-3-3 3zM16.5 9l-3 3 3 3H22V9h-5.5z"/></svg>
              <span className="nano-toolbar-tip">{inputEnabled ? '禁用控制' : '启用控制'}</span>
            </button>
            )}
            {/* Zoom In */}
            <button
              className="nano-toolbar-btn"
              onClick={() => { playClick(); onZoomIn?.(); }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/></svg>
              <span className="nano-toolbar-tip">放大 {Math.round(canvasZoom * 100)}% [Alt+= / Alt+滚轮↑]</span>
            </button>
            {/* Zoom Out */}
            <button
              className={`nano-toolbar-btn ${canvasZoom <= 1 ? 'disabled' : ''}`}
              onClick={() => { if (canvasZoom > 1) { playClick(); onZoomOut?.(); } }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7z"/></svg>
              <span className="nano-toolbar-tip">缩小 [Alt+- / Alt+滚轮↓]</span>
            </button>
            {/* Pan mode */}
            <button
              className={`nano-toolbar-btn ${isPanMode ? 'active' : ''} ${canvasZoom <= 1 ? 'disabled' : ''}`}
              onClick={() => { if (canvasZoom > 1) { playClick(); onTogglePanMode?.(); } }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z"/></svg>
              <span className="nano-toolbar-tip">{isPanMode ? '退出拖拽' : '拖拽画布'} [Alt+拖拽]</span>
            </button>
            {/* Reset zoom */}
            <button
              className={`nano-toolbar-btn ${canvasZoom <= 1 ? 'disabled' : ''}`}
              onClick={() => { if (canvasZoom > 1) { playClick(); onResetZoom?.(); } }}
              onMouseEnter={playHover}
            >
              <svg viewBox="0 0 24 24"><path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
              <span className="nano-toolbar-tip">重置画布 [Alt+中键]</span>
            </button>
          </div>
        )}
        {showShareDialog && (
          <div
            data-share-dialog
            style={{
              position: 'absolute',
              ...(isHorizontal ? { left: -3, top: 36 } : { left: 36, top: -3 }),
              background: 'rgba(20, 22, 28, 0.96)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              padding: 14,
              width: 240,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              zIndex: 3001,
              backdropFilter: 'blur(8px)',
            }}
          >
            <div style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>分享远程</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => { playClick(); setShareType('once'); }}
                style={{
                  flex: 1, padding: '6px 0', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                  border: shareType === 'once' ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.15)',
                  background: shareType === 'once' ? 'rgba(16,185,129,0.18)' : 'transparent',
                  color: shareType === 'once' ? '#10b981' : '#cbd5e1',
                }}
              >单次</button>
              <button
                onClick={() => { playClick(); setShareType('ttl'); }}
                style={{
                  flex: 1, padding: '6px 0', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                  border: shareType === 'ttl' ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.15)',
                  background: shareType === 'ttl' ? 'rgba(16,185,129,0.18)' : 'transparent',
                  color: shareType === 'ttl' ? '#10b981' : '#cbd5e1',
                }}
              >限时</button>
            </div>
            {shareType === 'ttl' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <input
                  type="number"
                  min="0.1"
                  max="24"
                  step="0.1"
                  value={shareHours}
                  onChange={(e) => setShareHours(e.target.value)}
                  style={{
                    flex: 1, padding: '6px 8px', fontSize: 12, borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.3)', color: '#e5e7eb',
                  }}
                />
                <span style={{ color: '#94a3b8', fontSize: 12 }}>小时 (0.1~24)</span>
              </div>
            )}
            <button
              disabled={shareLoading}
              onClick={handleShare}
              style={{
                width: '100%', padding: '8px 0', fontSize: 13, fontWeight: 600, borderRadius: 6,
                border: 'none', cursor: shareLoading ? 'default' : 'pointer',
                background: shareLoading ? 'rgba(16,185,129,0.4)' : '#10b981', color: '#fff',
              }}
            >{shareLoading ? '生成中…' : '生成链接并复制'}</button>
          </div>
        )}
      </div>

      {/* 7. Stats Panel */}
      <button
        className={`nano-btn ${statsActive ? 'active' : ''}`}
        style={{ '--btn-color': '#14b8a6' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleStats?.(); }}
        onMouseEnter={playHover}
        title="状态面板 [F10]"
      >
        <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
      </button>

      {/* 8. ICE Racing Panel — 面板打开时active，relay时显示警示点 */}
      <button
        className={`nano-btn ${iceRacingActive ? 'active' : ''}`}
        style={{ '--btn-color': '#f59e0b' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleIceRacing?.(); }}
        onMouseEnter={playHover}
        title="P2P竞选面板"
      >
        <svg viewBox="0 0 24 24"><path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 0 0 12 4c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
        {stats.transportType === 'relay' && !iceRacingActive && (
          <div className="nano-active-dot" style={{ background: '#f59e0b', boxShadow: '0 0 4px #f59e0b' }} />
        )}
      </button>

      {/* 9. Settings */}
      <button
        className={`nano-btn ${settingsActive ? 'active' : ''}`}
        style={{ '--btn-color': '#a855f7' } as React.CSSProperties}
        onClick={() => { playClick(); onOpenSettings?.(); }}
        onMouseEnter={playHover}
        title="设置 [F9]"
      >
        <svg viewBox="0 0 24 24"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/></svg>
      </button>

      {/* 8. File Manager */}
      {!viewOnly && (
      <button
        className={`nano-btn ${fileManagerActive ? 'active' : ''}`}
        style={{ '--btn-color': '#8b5cf6' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleFileManager?.(); }}
        onMouseEnter={playHover}
        title="文件管理"
      >
        <svg viewBox="0 0 24 24"><path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/></svg>
        {fileManagerActive && <div className="nano-active-dot" />}
      </button>
      )}

      {/* 9. Fullscreen */}
      <button
        className={`nano-btn ${isFullscreen ? 'active' : ''}`}
        style={{ '--btn-color': '#06b6d4' } as React.CSSProperties}
        onClick={() => { playClick(); onToggleFullscreen(); }}
        onMouseEnter={playHover}
        title={isFullscreen ? '退出全屏 [F11]' : '全屏 [F11]'}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
        ) : (
          <svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
        )}
      </button>

      <div className="nano-divider" />

      {/* 10. Disconnect */}
      <button
        className="nano-btn nano-btn-danger"
        style={{ '--btn-color': '#ef4444' } as React.CSSProperties}
        onClick={() => { playClick(); onDisconnect?.(); }}
        onMouseEnter={playHover}
        title="断开连接"
      >
        <svg viewBox="0 0 24 24"><path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z"/></svg>
      </button>
      </div>

      {/* Venue info: 网吧名称 / 备注或机号(seat) / IP / 分组 */}
      {showVenue && (
        <div className={`nano-blade-venue ${isHorizontal ? 'nano-blade-venue-h' : 'nano-blade-venue-v'}`}>
          {venueName && <span className="venue-name">{venueName}</span>}
          {terminalSeat && (
            <span className="venue-seat">
              <svg className="venue-seat-icon" viewBox="0 0 24 24"><path d="M20 3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h6l-1 1v2h6v-2l-1-1h6c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 13H4V5h16v11z"/></svg>
              <span className="venue-seat-text">{terminalSeat}</span>
            </span>
          )}
          {terminalIp && <span className="venue-ip">{terminalIp}</span>}
          {venueGroup && (
            <span className="venue-group"><span className="venue-group-dot" />{venueGroup}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(StatusBar);
